/**
 * Modal de gerenciamento de integrações de calendário
 * 
 * Lista todas as contas de calendário cadastradas no tenant,
 * permite conectar novas contas, desconectar e excluir permanentemente.
 * 
 * RELEASE: 2026-03-03
 * VERSION: 1.1.0 - Adicionado botão de exclusão permanente com AlertDialog
 */

import { useState } from 'react';
import { Calendar, Check, Unplug, Plus, Trash2, Loader2, AlertTriangle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import googleCalendarIcon from '@/assets/google-calendar-icon.png';
import { useCalendarIntegrations } from '@/hooks/useSchedulingEventTypes';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

const logger = createLogger('CalendarIntegrationsModal');

interface CalendarIntegrationsModalProps {
  /** Controla se o modal está aberto */
  open: boolean;
  /** Callback para abrir/fechar o modal */
  onOpenChange: (open: boolean) => void;
}

export function CalendarIntegrationsModal({ open, onOpenChange }: CalendarIntegrationsModalProps) {
  const { integrations, isLoading } = useCalendarIntegrations();
  const { currentTenant, user } = useAuth();
  const queryClient = useQueryClient();

  /** ID da integração pendente de confirmação de exclusão */
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  /** Mutation para desconectar uma integração (is_active = false) */
  const disconnectMutation = useMutation({
    mutationFn: async (integrationId: string) => {
      logger.info('Desconectando integração de calendário', { integrationId });
      const { error } = await supabase
        .from('calendar_integrations')
        .update({ is_active: false })
        .eq('id', integrationId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-integrations'] });
      queryClient.invalidateQueries({ queryKey: ['calendar-integration'] });
      toast.success('Calendário desconectado!');
    },
    onError: () => {
      toast.error('Erro ao desconectar calendário');
    },
  });

  /** Mutation para excluir permanentemente uma integração (DELETE) */
  const deleteMutation = useMutation({
    mutationFn: async (integrationId: string) => {
      logger.info('Excluindo integração de calendário permanentemente', { integrationId });
      const { error } = await supabase
        .from('calendar_integrations')
        .delete()
        .eq('id', integrationId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-integrations'] });
      queryClient.invalidateQueries({ queryKey: ['calendar-integration'] });
      toast.success('Calendário excluído permanentemente!');
      setDeleteConfirmId(null);
    },
    onError: () => {
      toast.error('Erro ao excluir calendário');
      setDeleteConfirmId(null);
    },
  });

  /** Inicia fluxo OAuth2 com Google Calendar via POST + popup */
  const handleConnect = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        logger.error('Usuário não autenticado para OAuth');
        toast.error('Usuário não autenticado');
        return;
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const redirectUri = window.location.origin + '/tenant/scheduling';

      logger.info('Iniciando fluxo OAuth Google Calendar via POST');

      const response = await fetch(
        `${supabaseUrl}/functions/v1/calendar-oauth?action=authorize`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
            'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          },
          body: JSON.stringify({ redirect_uri: redirectUri }),
        }
      );

      const data = await response.json();
      if (!response.ok || !data.auth_url) {
        logger.error('Erro ao obter URL de autorização Google', data);
        toast.error('Erro ao iniciar conexão com Google');
        return;
      }

      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      window.open(
        data.auth_url,
        'google-calendar-oauth',
        `width=${width},height=${height},left=${left},top=${top},menubar=no,toolbar=no`
      );
    } catch (error) {
      logger.error('Erro ao iniciar OAuth', error);
      toast.error('Erro ao conectar calendário');
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-primary" />
              Calendários
            </DialogTitle>
            <DialogDescription>
              Gerencie as contas de calendário conectadas a sua Conta.
              Cada tipo de evento pode ser vinculado a um calendário específico.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 max-h-[400px] overflow-y-auto">
            {/* Estado de carregamento */}
            {isLoading && (
              <div className="space-y-3">
                {[1, 2].map((i) => (
                  <div key={i} className="flex items-center gap-3 p-3 rounded-lg border border-border/50">
                    <Skeleton className="h-8 w-8 rounded-lg" />
                    <div className="flex-1 space-y-1">
                      <Skeleton className="h-4 w-40" />
                      <Skeleton className="h-3 w-24" />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Lista vazia */}
            {!isLoading && integrations.length === 0 && (
              <div className="text-center py-6 text-muted-foreground text-sm">
                Nenhum calendário conectado ainda.
              </div>
            )}

            {/* Lista de integrações */}
            {!isLoading && integrations.map((integration) => (
              <div
                key={integration.id}
                className="flex items-center gap-3 p-3 rounded-lg border border-border/50 bg-muted/30"
              >
                {/* Ícone do provider */}
                <div className="flex h-8 w-8 items-center justify-center shrink-0">
                  <img src={googleCalendarIcon} alt="Google Calendar" className="h-6 w-6" />
                </div>

                {/* Informações da integração com alerta para email vazio */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">
                    Google Calendar
                  </p>
                  {integration.provider_email ? (
                    <p className="text-xs text-muted-foreground truncate">
                      {integration.provider_email} • {integration.calendar_id || 'primary'}
                    </p>
                  ) : (
                    <div className="flex items-center gap-1 text-xs text-amber-600">
                      <AlertTriangle className="h-3 w-3 shrink-0" />
                      <span className="truncate">Email não identificado — reconectar</span>
                    </div>
                  )}
                </div>

                {/* Status badge */}
                {integration.is_active ? (
                  <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20 gap-1 shrink-0">
                    <Check className="h-3 w-3" />
                    Ativo
                  </Badge>
                ) : (
                  <Badge variant="secondary" className="shrink-0">Inativo</Badge>
                )}

                {/* Botão desconectar (só para ativos) */}
                {integration.is_active && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="shrink-0 text-muted-foreground hover:text-foreground"
                    onClick={() => disconnectMutation.mutate(integration.id)}
                    disabled={disconnectMutation.isPending}
                    title="Desconectar calendário"
                  >
                    <Unplug className="h-4 w-4" />
                  </Button>
                )}

                {/* Botão excluir permanentemente (sempre visível) */}
                <Button
                  variant="ghost"
                  size="icon"
                  className="shrink-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                  onClick={() => setDeleteConfirmId(integration.id)}
                  disabled={deleteMutation.isPending}
                  title="Excluir calendário permanentemente"
                >
                  {deleteMutation.isPending && deleteConfirmId === integration.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4" />
                  )}
                </Button>
              </div>
            ))}
          </div>

          {/* Botão conectar novo calendário */}
          <Button onClick={handleConnect} variant="outline" className="w-full gap-2 mt-2">
            <Plus className="h-4 w-4" />
            Conectar novo calendário Google
          </Button>
        </DialogContent>
      </Dialog>

      {/* AlertDialog de confirmação de exclusão */}
      <AlertDialog open={!!deleteConfirmId} onOpenChange={(open) => !open && setDeleteConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir calendário permanentemente?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. O calendário será removido permanentemente
              e qualquer tipo de evento vinculado a ele perderá a associação.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteConfirmId && deleteMutation.mutate(deleteConfirmId)}
            >
              Excluir permanentemente
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
